// 'Discounts without a manager PIN' on the device role page: the company's manual discounts
// (promoType 2), read page by page; the page owns which ones are switched on.
import React, { useCallback, useEffect, useState } from "react";
import { fetchPromotionsForDeviceUserRoleList } from "../../services/promotion.service";
import { BORDER_SOFT, TEXT, TEXT_DIM, TEXT_MUTED } from "../../components/list";
import { PromotionOption, manualDiscounts } from "./roleLogic";
import { Badge, FieldErrorText, H2, PANEL, PillButton, Switch, TextButton, fieldStyle } from "./roleShared";

export type PromotionLoadStatus = "loading" | "ready" | "error";

export function usePromotionOptions() {
  const [status, setStatus] = useState<PromotionLoadStatus>("loading");
  const [options, setOptions] = useState<PromotionOption[]>([]);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    fetchPromotionsForDeviceUserRoleList()
      .then((list) => {
        if (cancelled) return;
        setOptions(manualDiscounts(list));
        setStatus("ready");
      })
      .catch((error) => {
        console.error("Error loading promotions:", error);
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { status, options, retry };
}

type Props = {
  language: any;
  touch: boolean;
  status: PromotionLoadStatus;
  options: PromotionOption[];
  selectedIds: string[];
  hiddenCount: number;
  onChange: (ids: string[]) => void;
  onRetry: () => void;
};

export function PromotionTendersDeviceRole({ language, touch, status, options, selectedIds, hiddenCount, onChange, onRetry }: Props) {
  const [query, setQuery] = useState("");
  const tr = (key: string) => (language && language[key] ? language[key] : key);
  const fill = (key: string, values: Record<string, string | number>) =>
    Object.keys(values).reduce((text, name) => text.split(`{${name}}`).join(String(values[name])), tr(key));
  const term = query.trim().toLowerCase();
  const visible = term ? options.filter((p) => p.name.toLowerCase().includes(term)) : options;
  const selected = new Set(selectedIds);
  const onCount = options.filter((p) => selected.has(p.id)).length;
  const setMany = (ids: string[], on: boolean) => {
    const next = new Set(selectedIds);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    onChange(options.filter((p) => next.has(p.id)).map((p) => p.id));
  };

  return (
    <section aria-labelledby="device-discounts-title" style={{ ...PANEL, padding: touch ? "16px" : "20px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          <h2 id="device-discounts-title" style={{ ...H2, display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
            {tr("Discounts without a manager PIN")}
            {status === "ready" && (
              <span style={{ fontSize: "13px", fontWeight: 400, color: TEXT_DIM, fontVariantNumeric: "tabular-nums" }}>
                {fill("{n} of {m}", { n: onCount, m: options.length })}
              </span>
            )}
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: "14px", color: TEXT_MUTED }}>
            {tr("Staff with this role can apply these manual discounts directly. Other discounts ask for a manager's PIN.")}
          </p>
        </div>
        {status === "ready" && options.length > 0 && (
          <span style={{ display: "inline-flex", gap: "2px", marginLeft: "auto" }}>
            <TextButton onClick={() => setMany(visible.map((p) => p.id), true)} disabled={visible.every((p) => selected.has(p.id))}>
              {tr("All")}
            </TextButton>
            <TextButton onClick={() => setMany(visible.map((p) => p.id), false)} disabled={!visible.some((p) => selected.has(p.id))}>
              {tr("None")}
            </TextButton>
          </span>
        )}
      </div>
      {hiddenCount > 0 && status === "ready" && (
        <p style={{ margin: "8px 0 0", fontSize: "13px", color: TEXT_DIM }}>
          {hiddenCount === 1
            ? tr("1 other promotion linked to this role is not a manual discount. It is kept as it is.")
            : fill("{n} other promotions linked to this role are not manual discounts. They are kept as they are.", { n: hiddenCount })}
        </p>
      )}
      {status === "loading" && <p style={{ margin: "16px 0 0", fontSize: "14px", color: TEXT_DIM }}>{tr("Loading discounts…")}</p>}
      {status === "error" && (
        <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "10px" }}>
          <FieldErrorText>{tr("Discounts could not be loaded. Saving keeps this role's discounts as they are.")}</FieldErrorText>
          <PillButton small onClick={onRetry}>
            {tr("Try again")}
          </PillButton>
        </div>
      )}
      {status === "ready" && options.length === 0 && (
        <p style={{ margin: "16px 0 0", fontSize: "14px", color: TEXT_DIM }}>{tr("This company has no manual discounts yet.")}</p>
      )}
      {status === "ready" && options.length > 0 && (
        <>
          <input
            type="search"
            className="role-field"
            aria-label={tr("Search discounts")}
            placeholder={tr("Search discounts")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) e.preventDefault();
            }}
            style={{ ...fieldStyle(touch), maxWidth: touch ? undefined : "280px", height: touch ? "44px" : "40px", marginTop: "16px" }}
          />
          <div style={{ marginTop: "12px", border: `1px solid ${BORDER_SOFT}`, borderRadius: "12px", overflow: "hidden" }}>
            {visible.length === 0 && (
              <p style={{ margin: 0, padding: "12px", fontSize: "14px", color: TEXT_DIM }}>{tr("No discounts match your search.")}</p>
            )}
            {visible.map((p, index) => {
              const on = selected.has(p.id);
              return (
                <div
                  key={p.id}
                  className="role-row"
                  onClick={() => setMany([p.id], !on)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    minHeight: touch ? "52px" : "44px",
                    padding: "8px 12px",
                    borderTop: index === 0 ? "none" : `1px solid ${BORDER_SOFT}`,
                    cursor: "pointer",
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: touch ? "15px" : "14px", fontWeight: 500, color: TEXT, overflowWrap: "anywhere" }}>
                      {p.name || tr("Untitled discount")}
                    </span>
                    {!p.isActive && <Badge label={tr("Inactive")} kind="off" />}
                  </span>
                  <Switch checked={on} label={p.name || tr("Untitled discount")} touch={touch} onChange={(next) => setMany([p.id], next)} />
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
